import { APP_NAME, SEO_COPY, SUPPORTED_LOCALES, type Locale } from "@/constants/config";
import { DASHBOARD_ROUTE, publicLocalePath, workspaceSectionPath } from "@/constants/routes";
import { PLAN_DESCRIPTIONS, PLAN_LABELS, PLANS } from "@/constants/plans";
import { PublicFooter } from "@/components/layout/public-footer";
import { PublicNavbar } from "@/components/layout/public-navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import GradientButton from "@/components/kokonutui/gradient-button";
import SlicedText from "@/components/kokonutui/sliced-text";
import { Seo } from "@/lib/seo";
import { getDictionary } from "@bizy/core/i18n/resolve";
import { useDemoWorkspace } from "@/hooks/use-demo-workspace";
import { motion } from "motion/react";
import { ArrowRight, Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const FEATURE_ICONS = ["🛒", "💸", "🛡️", "🧾", "🖼️", "🖨️"] as const;

export interface LandingProps {
  locale: Locale;
}

/** Public branding page (PRD §2.1) — localized, SEO-injected, fully responsive. */
export const Landing = ({ locale }: LandingProps) => {
  const dictionary = getDictionary(locale);
  const copy = SEO_COPY[locale];
  const navigate = useNavigate();
  const demo = useDemoWorkspace();
  // Preview build: both hero CTAs land in the demo workspace dashboard.
  const dashboardTarget = demo.slug
    ? workspaceSectionPath(demo.slug, "home")
    : DASHBOARD_ROUTE;

  return (
    <div className="min-h-screen">
      <Seo
        title={copy.title}
        description={copy.description}
        keywords={copy.keywords}
        path={`${publicLocalePath(locale)}/`}
        locale={locale}
        alternates={SUPPORTED_LOCALES.map((value) => ({
          hreflang: value,
          path: `${publicLocalePath(value)}/`,
        }))}
      />
      <PublicNavbar />

      <section className="relative overflow-hidden border-b border-border">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(60% 60% at 15% 10%, color-mix(in oklab, var(--primary) 22%, transparent), transparent), radial-gradient(50% 50% at 85% 20%, color-mix(in oklab, var(--accent) 20%, transparent), transparent)",
          }}
        />
        <div className="relative mx-auto grid w-full max-w-6xl gap-10 px-4 py-16 lg:grid-cols-2 lg:py-24">
          <div className="space-y-6">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1 text-xs font-medium">
              <span className="size-1.5 rounded-full bg-primary" />
              {dictionary.hero.badge}
            </span>

            <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl">
              {dictionary.hero.title}
              <br />
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                {dictionary.hero.highlight}
              </span>
            </h1>

            <p className="max-w-xl text-base text-muted-foreground sm:text-lg">
              {dictionary.hero.description}
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="lg">
                <Link to={dashboardTarget}>
                  {dictionary.hero.primaryCta}
                  <ArrowRight className="size-4" aria-hidden />
                </Link>
              </Button>
              <GradientButton onClick={() => navigate(dashboardTarget)}>
                {dictionary.hero.secondaryCta}
              </GradientButton>
            </div>

            <dl className="grid grid-cols-3 gap-4 pt-4">
              {dictionary.hero.stats.map((stat) => (
                <div key={stat.label} className="space-y-1">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    {stat.label}
                  </dt>
                  <dd className="text-sm font-semibold">{stat.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="relative"
          >
            <Card className="overflow-hidden">
              <CardContent className="space-y-4 p-5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="font-medium">ORD-202610-00002</span>
                  <span>DP · partially paid</span>
                </div>
                <div className="space-y-2 text-sm">
                  {[
                    { name: "Buffet set", qty: 2, amount: "Rp 900.000" },
                    { name: "Round table 180cm", qty: 2, amount: "Rp 300.000" },
                  ].map((line) => (
                    <div key={line.name} className="flex items-center justify-between gap-3">
                      <span className="truncate">{line.name}</span>
                      <span className="text-muted-foreground">×{line.qty}</span>
                      <span className="tabular-nums">{line.amount}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 border-t border-border pt-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total</span>
                    <span className="tabular-nums">Rp 1.200.000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Paid (DP)</span>
                    <span className="tabular-nums">Rp 400.000</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Balance</span>
                    <span className="tabular-nums">Rp 800.000</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="flex-1">
                    Print A4
                  </Button>
                  <Button size="sm" className="flex-1">
                    Share WhatsApp
                  </Button>
                </div>
              </CardContent>
            </Card>
            <div className="absolute -bottom-4 -right-4 hidden sm:block">
              <SlicedText text={APP_NAME} className="text-2xl font-bold" />
            </div>
          </motion.div>
        </div>
      </section>

      <section id="features" className="mx-auto w-full max-w-6xl px-4 py-16">
        <div className="max-w-2xl space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {dictionary.features.title}
          </h2>
          <p className="text-sm text-muted-foreground sm:text-base">
            {dictionary.features.description}
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {dictionary.features.items.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.35, delay: index * 0.05 }}
            >
              <Card className="h-full">
                <CardContent className="space-y-2 p-5">
                  <span className="text-xl" aria-hidden>
                    {FEATURE_ICONS[index] ?? "✨"}
                  </span>
                  <h3 className="font-medium">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="workflow" className="border-y border-border bg-muted/30">
        <div className="mx-auto w-full max-w-6xl px-4 py-16">
          <div className="max-w-2xl space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {dictionary.workflow.title}
            </h2>
            <p className="text-sm text-muted-foreground sm:text-base">
              {dictionary.workflow.description}
            </p>
          </div>

          <ol className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {dictionary.workflow.steps.map((step, index) => (
              <li key={step.title} className="rounded-card border border-border bg-card p-5">
                <span className="text-xs font-semibold text-primary">0{index + 1}</span>
                <h3 className="mt-2 font-medium">{step.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="plans" className="mx-auto w-full max-w-6xl px-4 py-16">
        <div className="max-w-2xl space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {dictionary.plans.title}
          </h2>
          <p className="text-sm text-muted-foreground sm:text-base">{dictionary.plans.description}</p>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="space-y-3 p-6">
              <h3 className="text-lg font-semibold">{PLAN_LABELS[PLANS.free]}</h3>
              <p className="text-sm text-muted-foreground">{PLAN_DESCRIPTIONS[PLANS.free]}</p>
              <ul className="space-y-2 text-sm">
                {dictionary.plans.free.description.split(", ").map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <Check className="size-4 text-primary" aria-hidden />
                    {line}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="w-full">
                <Link to={DASHBOARD_ROUTE}>{dictionary.plans.cta}</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-primary/40">
            <CardContent className="space-y-3 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{PLAN_LABELS[PLANS.pro]}</h3>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                  {dictionary.plans.comingSoon}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{PLAN_DESCRIPTIONS[PLANS.pro]}</p>
              <ul className="space-y-2 text-sm">
                {dictionary.plans.pro.description.split(", ").map((line) => (
                  <li key={line} className="flex items-center gap-2">
                    <Check className="size-4 text-primary" aria-hidden />
                    {line}
                  </li>
                ))}
              </ul>
              {/* Checkout stays hidden until a gateway ships (BILLING_UI_ENABLED). */}
              <Button disabled className="w-full">
                {dictionary.plans.comingSoon}
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-4 px-4 py-16 text-center">
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {dictionary.cta.title}
          </h2>
          <p className="max-w-xl text-sm text-muted-foreground sm:text-base">
            {dictionary.cta.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <Link to={dashboardTarget}>{dictionary.cta.primary}</Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to={dashboardTarget}>{dictionary.cta.secondary}</Link>
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter locale={locale} />
    </div>
  );
};
